import { JSONCodec, NatsError, nuid } from "nats.ws";
import type { NatsConnection } from "nats.ws";

import type { ApiCaller, ApiCallerOptions } from "../ApiCaller";
import type { CallContext, ConnectionContext, Subject } from "../context";
import { ApiError, TimeoutError } from "../error";
import { handleResponse } from "../response";
import { resolveSubject, wireSubject, withoutRole } from "../subjectVars";
import { keysToSnake, keysToCamel } from "../transform";
import { toNatsHeaders } from "./natsHeaders";

export class NatsApiCaller implements ApiCaller {
  private readonly codec = JSONCodec();

  constructor(
    private connectionContext: ConnectionContext<NatsConnection>,
    private subjectVars: Record<string, string>,
    private contextFactory: (context: CallContext) => CallContext,
  ) {}

  /**
   * When schema is not set, the backend returns the response untouched — no
   * snake_case→camelCase key conversion and no Zod schema parsing. Use for
   * endpoints whose response carries opaque/dynamic ALM-specific keys (e.g.
   * `ac_x_elementName`) that the key conversion would mangle, so the caller
   * gets WAMP-shaped (snake_case) data.
   *
   * When rawRequest is set, the payload is sent as-is — no camelCase→snake_case
   * key conversion. Use for endpoints whose request carries opaque/dynamic
   * ALM-specific keys (e.g. `ac_x_elementName`, `referenceName`) that
   * the key conversion would mangle; the caller must pass the wire (snake_case)
   * shape itself.
   */
  async request<T>(
    subject: Subject,
    action: string,
    payload: unknown,
    options?: ApiCallerOptions<T>,
  ): Promise<T> {
    const res = await this.send(subject, action, payload, options);
    return options?.schema ? options.schema.parse(keysToCamel(res)) : (res as T);
  }

  private async send(
    subject: Subject,
    action: string,
    payload: unknown,
    options?: { timeout?: number; publish?: boolean; rawRequest?: boolean },
  ): Promise<unknown> {
    const scheme = this.connectionContext.scheme ?? "v1";
    const wire = wireSubject(subject, scheme);
    // resolveSubject throws on a var the template lacks; only a v2
    // template carries a role token, so the template decides, not the scheme
    const vars = wire.template.includes("${role}")
      ? this.subjectVars
      : withoutRole(this.subjectVars);
    const resolved = resolveSubject(wire, vars);
    const context = this.contextFactory({
      subject: resolved,
      action,
      payload,
      requestTimeout: options?.timeout,
      correlationId: "",
      clientId: this.connectionContext.clientId,
      issuedAt: new Date().toISOString(),
    });
    try {
      const encoded = this.codec.encode(options?.rawRequest ? payload : keysToSnake(payload));
      const connection = this.connectionContext.connection;
      if (options?.publish) {
        return connection.publish(resolved, encoded, { headers: toNatsHeaders(context) });
      }
      const requestOptions = {
        timeout: context.requestTimeout ?? this.connectionContext.defaultTimeout,
        reply: `_INBOX.${this.connectionContext.userId}.${context.action}.${nuid.next()}`,
        noMux: true,
        headers: toNatsHeaders(context),
      };
      const rawResponse = await connection.request(resolved, encoded, requestOptions);
      const response = this.codec.decode(rawResponse.data);
      return handleResponse(response, context);
    } catch (err: unknown) {
      handleError(err, context);
    }
  }
}

function handleError(err: unknown, context: CallContext): never {
  if (err instanceof NatsError) {
    if (err.code === "TIMEOUT") {
      throw new TimeoutError({
        request: context,
        response: {},
      });
    }
    throw new ApiError(err.name, `Nats Error while calling RPC method ${context.action}`, {
      request: context,
      response: {
        message: err.message,
        code: err.code,
        cause: err.chainedError,
      },
    });
  }
  throw err;
}
